import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SearchIcon from '@mui/icons-material/Search';
import { Alert, Box, Chip, Link, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from "@mui/material";
import { useRef, useState } from "react";
import { BELEG_HINT, BELEG_LABELS, DASH, EINSCHAETZUNG_LABELS, FAZIT_HINT, FAZIT_LABELS, fmtCount, fmtDate, fmtNumber, fmtPctSigned, fmtPercentValueSigned, germanizeText, INTERNAL_ORIGINS,
    normalizeDossierContent, normalizeSources, splitCitations } from "./opportunityFormat";

/** Text des Dossiers ist fremder Inhalt und wird nur als React-Text ausgegeben, nie als HTML. */
function CitedText({ text, sourceIds, onCite }) {
    return splitCitations(text).map((part, index) => {
        if (part.type === 'text') return <span key={index}>{germanizeText(part.value)}</span>;
        return <Citation key={index} id={part.id} bare={part.bare} sourceIds={sourceIds} onCite={onCite} />;
    });
}

function Citation({ id, bare, sourceIds, onCite }) {
    if (INTERNAL_ORIGINS[id]) {
        return (
            <Tooltip title={`Herkunft: eigene ${INTERNAL_ORIGINS[id]} aus Finalyser, keine externe Quelle`} arrow>
                <Typography component="span" variant="caption" sx={{
                    mx: 0.25, px: 0.5, borderRadius: 0.5, border: 1, borderColor: 'divider', whiteSpace: 'nowrap',
                }}>
                    {INTERNAL_ORIGINS[id]}
                </Typography>
            </Tooltip>
        );
    }
    if (!sourceIds.has(id)) {
        if (bare) return <span>{id}</span>;
        return <Typography component="span" variant="caption" sx={{ mx: 0.25 }}>{id}</Typography>;
    }
    return (
        <Link
            component="button"
            type="button"
            variant="caption"
            color="secondary"
            onClick={() => onCite(id)}
            aria-label={`Zur Quelle ${id}`}
            sx={{ mx: 0.25, fontWeight: 'bold', verticalAlign: 'baseline' }}
        >
            {id}
        </Link>
    );
}

/** Quellen aus der Angabe, die nicht schon im Text genannt sind. */
function ExtraCitations({ entry, sourceIds, onCite }) {
    const inline = new Set(splitCitations(entry.text).filter((part) => part.type === 'cite').map((part) => part.id));
    const extra = entry.quellen.filter((id) => !inline.has(id));
    if (!extra.length) return null;
    return (
        <Typography component="span" variant="caption" sx={{ ml: 0.5 }}>
            Herkunft:{extra.map((id) => <Citation key={id} id={id} sourceIds={sourceIds} onCite={onCite} />)}
        </Typography>
    );
}

function Question({ title, children, chips }) {
    return (
        <Box sx={{ mb: 2 }}>
            <Stack direction="row" alignItems="center" flexWrap="wrap" sx={{ gap: 1, mb: 0.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>{title}</Typography>
                {chips}
            </Stack>
            {children}
        </Box>
    );
}

function EntryText({ entry, sourceIds, onCite }) {
    if (!entry) return <Typography variant="body2" sx={{ opacity: 0.75 }}>Keine Angabe im Dossier.</Typography>;
    return (
        <Typography variant="body2">
            <CitedText text={entry.text} sourceIds={sourceIds} onCite={onCite} />
            <ExtraCitations entry={entry} sourceIds={sourceIds} onCite={onCite} />
        </Typography>
    );
}

function EntryList({ entries, sourceIds, onCite }) {
    if (!entries.length) return <Typography variant="body2" sx={{ opacity: 0.75 }}>Keine Angabe im Dossier.</Typography>;
    return (
        <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {entries.map((entry, index) => (
                <Typography component="li" variant="body2" key={index} sx={{ mb: 0.5 }}>
                    <CitedText text={entry.text} sourceIds={sourceIds} onCite={onCite} />
                    <ExtraCitations entry={entry} sourceIds={sourceIds} onCite={onCite} />
                </Typography>
            ))}
        </Box>
    );
}

function BelegChip({ value }) {
    if (!value) return null;
    return <Chip size="small" variant="outlined" label={`Beleg: ${BELEG_LABELS[value]}`} />;
}

function rangeLabel(row) {
    if (row.von) return `${fmtDate(row.von)} bis ${fmtDate(row.bis)}`;
    return row.label ? germanizeText(row.label) : 'Zeitraum unbekannt';
}

/** Rückgangsphasen mit Zeitraum, Höhe, Erklärung, Belegstatus und Quellen. */
function DeclineTimeline({ rows, sourceIds, onCite }) {
    if (!rows.length) return null;
    return (
        <Box sx={{ mt: 0.5, mb: 1.5 }}>
            {rows.map((row, index) => (
                <Box key={`${row.von || row.label}-${index}`} sx={{ position: 'relative', pl: 2.5, pb: index === rows.length - 1 ? 0 : 1.5 }}>
                    <Box sx={{
                        position: 'absolute', left: 4, top: 6, bottom: index === rows.length - 1 ? 'auto' : -6, height: index === rows.length - 1 ? 8 : 'auto',
                        borderLeft: 1, borderColor: 'divider',
                    }} />
                    <Box sx={{ position: 'absolute', left: 0, top: 5, width: 9, height: 9, borderRadius: '50%', backgroundColor: 'text.secondary' }} />
                    <Stack direction="row" alignItems="center" flexWrap="wrap" sx={{ gap: 1, mb: 0.25 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{rangeLabel(row)}</Typography>
                        {row.change !== null && (
                            <Typography variant="body2" sx={{ fontWeight: 'bold', fontVariantNumeric: 'tabular-nums' }}>{fmtPctSigned(row.change)}</Typography>
                        )}
                        {row.monthly && <Typography variant="caption" sx={{ opacity: 0.75 }}>nach Monatskursen</Typography>}
                        <BelegChip value={row.entry?.belegt} />
                    </Stack>
                    {row.entry ? (
                        <EntryText entry={row.entry} sourceIds={sourceIds} onCite={onCite} />
                    ) : (
                        <Typography variant="body2" sx={{ opacity: 0.75 }}>Das Dossier enthält dazu keine Erklärung.</Typography>
                    )}
                </Box>
            ))}
        </Box>
    );
}

const compactCell = { py: 0.5, px: 1, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' };

function CompactTable({ headers, children }) {
    return (
        <TableContainer>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        {headers.map((header, index) => (
                            <TableCell key={header} align={index === 0 ? 'left' : 'right'} sx={compactCell}>{header}</TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>{children}</TableBody>
            </Table>
        </TableContainer>
    );
}

/** Eigene Kursdaten und Berichtstermine. Sie zeigen den zeitlichen Zusammenhang, keine Ursache. */
function PriceFacts({ content }) {
    const { tagesverluste, berichtstermine, schaetzungen, kursstand } = content;
    if (!tagesverluste.length && !berichtstermine.length && !schaetzungen.length && !kursstand) return null;
    const lossNearTermin = new Set(tagesverluste.map((row) => row.termin).filter(Boolean));
    return (
        <Box sx={{ mb: 2 }}>
            <Typography variant="caption" sx={{ display: 'block', opacity: 0.75, mb: 1 }}>
                Aus den eigenen Kursdaten und Berichtsterminen. Sie zeigen den zeitlichen Zusammenhang, keine Ursache.
                {kursstand && ` Tageskurse bis ${fmtDate(kursstand.daily)}, danach nur Monatskurse bis ${fmtDate(kursstand.all)}.`}
            </Typography>
            {tagesverluste.length > 0 && (
                <Box sx={{ mb: 1.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.25 }}>Größte Tagesverluste</Typography>
                    {tagesverluste.map((row) => (
                        <Stack key={row.datum} direction="row" alignItems="center" flexWrap="wrap" sx={{ gap: 1, py: 0.25 }}>
                            <Typography variant="body2" sx={{ minWidth: 76 }}>{fmtDate(row.datum)}</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', minWidth: 56, fontVariantNumeric: 'tabular-nums' }}>{fmtPctSigned(row.veraenderung)}</Typography>
                            {row.termin && <Chip size="small" color="warning" variant="outlined" label={`Berichtstermin nahe: ${fmtDate(row.termin)}`} />}
                        </Stack>
                    ))}
                </Box>
            )}
            {berichtstermine.length > 0 && (
                <Box sx={{ mb: 1.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.25 }}>Berichtstermine</Typography>
                    <CompactTable headers={['Termin', 'Quartal', 'EPS', 'Erwartet', 'Überraschung', 'Kurs 2 Tage']}>
                        {berichtstermine.map((row) => (
                            <TableRow key={row.termin}>
                                <TableCell sx={compactCell}>
                                    {fmtDate(row.termin)}{lossNearTermin.has(row.termin) ? ' (Tagesverlust nahe)' : ''}
                                </TableCell>
                                <TableCell align="right" sx={compactCell}>{row.quartal ? fmtDate(row.quartal) : DASH}</TableCell>
                                <TableCell align="right" sx={compactCell}>{fmtNumber(row.eps, 2)}</TableCell>
                                <TableCell align="right" sx={compactCell}>{fmtNumber(row.erwartet, 2)}</TableCell>
                                <TableCell align="right" sx={compactCell}>{fmtPercentValueSigned(row.ueberraschung)}</TableCell>
                                <TableCell align="right" sx={compactCell}>{fmtPctSigned(row.kurs2Tage)}</TableCell>
                            </TableRow>
                        ))}
                    </CompactTable>
                </Box>
            )}
            {schaetzungen.length > 0 && (
                <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.25 }}>Änderung der Analystenschätzungen</Typography>
                    <CompactTable headers={['Zeitraum', 'Schätzung', '30 Tage', '90 Tage', 'Hoch / runter (30 Tage)']}>
                        {schaetzungen.map((row) => (
                            <TableRow key={row.zeitraum}>
                                <TableCell sx={compactCell}>{row.zeitraum}</TableCell>
                                <TableCell align="right" sx={compactCell}>{fmtNumber(row.schaetzung, 2)}</TableCell>
                                <TableCell align="right" sx={compactCell}>{fmtPctSigned(row.aenderung30)}</TableCell>
                                <TableCell align="right" sx={compactCell}>{fmtPctSigned(row.aenderung90)}</TableCell>
                                <TableCell align="right" sx={compactCell}>{fmtCount(row.hoch)} / {fmtCount(row.runter)}</TableCell>
                            </TableRow>
                        ))}
                    </CompactTable>
                </Box>
            )}
        </Box>
    );
}

function stateNotice({ state, dossier, budget, isDataCheck }) {
    const olderDossier = dossier ? ` Angezeigt wird das Dossier vom ${fmtDate(dossier.created_at)}.` : '';
    if (state === 'skipped_budget') {
        const limit = budget ? ` (${budget} je Lauf)` : '';
        return {
            severity: 'info',
            text: `In diesem Lauf wurde kein neues Dossier erstellt, weil das Budget${limit} ausgeschöpft war. Das sagt nichts über das Papier aus. Die Reihenfolge folgt dem Abstand zur eigenen Historie.${olderDossier}`,
        };
    }
    if (state === 'failed' || dossier?.status === 'failed') {
        return {
            severity: 'warning',
            text: `Die Erstellung des Dossiers ist in diesem Lauf fehlgeschlagen, zum Beispiel weil die Antwort des Modells die Prüfung nicht bestanden hat. Es gibt daher keine neue Einschätzung, der nächste Lauf versucht es erneut.${dossier && dossier.status !== 'failed' ? olderDossier : ''}`,
        };
    }
    if (!dossier) {
        return {
            severity: 'info',
            text: isDataCheck
                ? 'Für Papiere unter „Daten prüfen“ wird kein Dossier erstellt.'
                : 'Für dieses Papier liegt kein Dossier vor.',
        };
    }
    return null;
}

const DossierView = ({ dossier, state, budget, isDataCheck }) => {
    const [highlight, setHighlight] = useState(null);
    const sourceRefs = useRef({});
    const notice = stateNotice({ state, dossier, budget, isDataCheck });
    const isNoSources = dossier?.status === 'no_sources' || (state === 'no_sources' && dossier);
    const showContent = dossier && dossier.status !== 'failed';
    const content = normalizeDossierContent(dossier?.content);
    const sources = normalizeSources(dossier?.sources);
    const sourceIds = new Set(sources.map((source) => source.id));
    const removed = Array.isArray(dossier?.warnings) ? dossier.warnings.length : 0;

    const onCite = (id) => {
        setHighlight(id);
        sourceRefs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };

    return (
        <Box>
            {notice && <Alert severity={notice.severity} sx={{ mb: 2 }}>{notice.text}</Alert>}
            {isNoSources && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                        {content.hinweis || 'Für dieses Papier wurden keine verwertbaren Quellen gefunden.'}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                        Ohne Quellen wird das Modell nicht gefragt. Die Einordnung bleibt hier ganz der eigenen Prüfung überlassen.
                    </Typography>
                </Alert>
            )}
            {showContent && isNoSources && (
                <>
                    <DeclineTimeline rows={content.zeitleiste} sourceIds={sourceIds} onCite={onCite} />
                    <PriceFacts content={content} />
                </>
            )}
            {showContent && !isNoSources && (
                <>
                    <Typography variant="caption" sx={{ display: 'block', opacity: 0.75, mb: 1.5 }}>
                        KI-Zusammenfassung vom {fmtDate(dossier.created_at)}. Sie kann Fehler enthalten, die Quellen selbst prüfen.
                        {removed > 0 && ` Bei der Prüfung ${removed === 1 ? 'wurde 1 Angabe' : `wurden ${removed} Angaben`} ohne Beleg entfernt.`}
                    </Typography>
                    {content.fazit && (
                        <Alert
                            severity={content.fazit.einordnung === 'genauer_pruefen' ? 'success' : 'info'}
                            icon={content.fazit.einordnung === 'genauer_pruefen' ? <SearchIcon fontSize="inherit" /> : undefined}
                            sx={{ mb: 2 }}
                        >
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Fazit: {FAZIT_LABELS[content.fazit.einordnung].label}</Typography>
                            <EntryText entry={content.fazit.text ? content.fazit : null} sourceIds={sourceIds} onCite={onCite} />
                            <Typography variant="caption" sx={{ display: 'block', opacity: 0.75, mt: 0.5 }}>{FAZIT_HINT}</Typography>
                        </Alert>
                    )}
                    <Question title="Warum ist der Kurs gefallen?" chips={<BelegChip value={content.warum?.belegt} />}>
                        <Typography variant="caption" sx={{ display: 'block', opacity: 0.75, mb: 1 }}>{BELEG_HINT}</Typography>
                        <DeclineTimeline rows={content.zeitleiste} sourceIds={sourceIds} onCite={onCite} />
                        <EntryText entry={content.warum} sourceIds={sourceIds} onCite={onCite} />
                    </Question>
                    <PriceFacts content={content} />
                    <Question
                        title="Vorübergehend oder dauerhaft?"
                        chips={(
                            <>
                                {content.dauer?.einschaetzung && (
                                    <Chip size="small" label={`Einschätzung: ${EINSCHAETZUNG_LABELS[content.dauer.einschaetzung]}`} />
                                )}
                                <BelegChip value={content.dauer?.belegt} />
                            </>
                        )}
                    >
                        <EntryText entry={content.dauer} sourceIds={sourceIds} onCite={onCite} />
                    </Question>
                    <Question title="Was dagegen spricht">
                        <EntryList entries={content.dagegen} sourceIds={sourceIds} onCite={onCite} />
                    </Question>
                    <Question title="Was die Idee widerlegen würde">
                        <EntryList entries={content.widerlegen} sourceIds={sourceIds} onCite={onCite} />
                    </Question>
                    <Question title="Unklar">
                        <EntryList entries={content.unklar} sourceIds={sourceIds} onCite={onCite} />
                    </Question>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0.5 }}>Quellen</Typography>
                    {sources.length === 0 && <Typography variant="body2" sx={{ opacity: 0.75 }}>Keine Quellen hinterlegt.</Typography>}
                    {sources.map((source) => (
                        <Box
                            key={source.id}
                            ref={(element) => { sourceRefs.current[source.id] = element; }}
                            sx={{
                                display: 'flex', gap: 1, py: 0.5, px: 0.5, borderRadius: 1,
                                backgroundColor: highlight === source.id ? 'action.selected' : 'transparent',
                            }}
                        >
                            <Typography variant="body2" sx={{ fontWeight: 'bold', minWidth: 28 }}>{source.id}</Typography>
                            <Box sx={{ minWidth: 0 }}>
                                {source.url ? (
                                    <Link
                                        href={source.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        color="secondary"
                                        variant="body2"
                                        sx={{ overflowWrap: 'anywhere' }}
                                    >
                                        {source.title}
                                        <OpenInNewIcon sx={{ fontSize: 13, ml: 0.5, verticalAlign: 'text-bottom' }} />
                                    </Link>
                                ) : (
                                    <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{source.title} (ohne nutzbaren Link)</Typography>
                                )}
                                <Typography variant="caption" sx={{ display: 'block', opacity: 0.75 }}>
                                    {source.kind === 'article' ? 'Artikel' : 'Nur Kurzfassung'}
                                    {`, ${source.published ? fmtDate(source.published) : 'Datum unbekannt'}`}
                                    {source.origin && `, ${source.origin}`}
                                    {source.window && `, ${source.window}`}
                                </Typography>
                            </Box>
                        </Box>
                    ))}
                    <Typography variant="caption" sx={{ display: 'block', opacity: 0.75, mt: 0.5 }}>
                        „Kennzahlen“ und „Kursdaten“ als Herkunft bedeuten: Die Angabe stammt aus den eigenen Finalyser-Daten, nicht aus einer externen Quelle.
                    </Typography>
                </>
            )}
        </Box>
    );
};

export default DossierView;
